import pluralize from 'pluralize';
import { Ingredient } from '../types/mealPlan';
import { formatQuantity, formatQuantityAsFraction } from './formatQuantity';

/** The fields that make up a displayed ingredient line (also satisfied by substitutions). */
type IngredientLine = Pick<Ingredient, 'name' | 'quantity' | 'unit' | 'preparation'>;

const isCanUnit = (unit: string): boolean => /^cans?$/i.test(unit.trim());

/**
 * Formats an ingredient name with proper pluralization based on quantity and unit
 * For ingredients with unit "each" and quantity > 1, pluralizes the name
 * 
 * @param ingredient - The ingredient object containing name, quantity, and unit
 * @returns The properly formatted ingredient name
 * 
 * @example
 * formatIngredientName({ name: "egg", quantity: 2, unit: "each" }) // returns "eggs"
 * formatIngredientName({ name: "egg", quantity: 1, unit: "each" }) // returns "egg"
 * formatIngredientName({ name: "flour", quantity: 2, unit: "cup" }) // returns "flour"
 * formatIngredientName({ name: "canned corn", quantity: 1, unit: "can" }) // returns "corn"
 */
export const formatIngredientName = (ingredient: IngredientLine): string => {
  if (ingredient.unit === 'each' && ingredient.quantity > 1) {
    return pluralize(ingredient.name);
  }
  // The unit already says it's canned: "1 can corn", not "1 can canned corn".
  // Display only — the stored name stays "canned corn", which is what tells
  // it apart from fresh corn for shopping.
  if (isCanUnit(ingredient.unit)) {
    return ingredient.name.replace(/^\s*(canned|tinned)\s+(?=\S)/i, '');
  }
  return ingredient.name;
};

/**
 * A can size leading an ingredient's preparation ("14.5 oz can, undrained").
 * The backend puts it there for every ingredient measured in cans, since most
 * canned goods are sold in more than one size.
 */
const LEADING_CAN_SIZE =
  /^\s*(\d+(?:\.\d+)?)\s*-?\s*(fluid ounces?|fl\.?\s*oz|ounces?|oz|pounds?|lbs?)\.?\s+cans?\b[\s,;]*/i;

const parseLeadingCanSize = (
  preparation: string | undefined
): { size: string; rest: string } | null => {
  const match = preparation?.match(LEADING_CAN_SIZE);
  if (!match) return null;
  const unit = match[2].toLowerCase();
  const abbreviation = unit.startsWith('f')
    ? 'fl oz'
    : unit.startsWith('o')
      ? 'oz'
      : 'lb';
  return {
    // Same fraction glyphs as every other quantity: "15 ¼ oz", not "15.25 oz"
    size: `${formatQuantityAsFraction(parseFloat(match[1]))} ${abbreviation}`,
    rest: preparation!.slice(match[0].length).trim(),
  };
};

/**
 * The amount shown for an ingredient. A line measured in cans carries its can
 * size in the amount, the way recipes conventionally write it:
 * "1 (14.5 oz) can" rather than "1 can" plus a size tucked into the prep note.
 */
export const formatIngredientQuantity = (ingredient: IngredientLine): string => {
  const quantity = formatQuantity(ingredient.quantity, ingredient.unit);
  if (!isCanUnit(ingredient.unit)) return quantity;
  const canSize = parseLeadingCanSize(ingredient.preparation);
  if (!canSize) return quantity;
  // "2 cans" -> "2 (15 oz) cans"
  return quantity.replace(/\s(cans?)$/, ` (${canSize.size}) $1`);
};

/**
 * The preparation note shown after an ingredient, or undefined when there is
 * none. When {@link formatIngredientQuantity} has lifted the can size into the
 * amount, it is dropped here so it isn't said twice. A line that was measured
 * in cans but has since been scaled into ounces keeps it as the size of the
 * cans to buy.
 */
export const formatIngredientPreparation = (
  ingredient: IngredientLine
): string | undefined => {
  const canSize = parseLeadingCanSize(ingredient.preparation);
  if (!canSize) return ingredient.preparation || undefined;
  if (isCanUnit(ingredient.unit)) return canSize.rest || undefined;
  return [`from ${canSize.size} cans`, canSize.rest].filter(Boolean).join(', ');
};
